import React, { useRef, useEffect, useState, memo } from 'react';
import { loadImage, getCachedImage } from './imageCache';
import { getRefImage, getCachedThumbnail, setCachedThumbnail } from './thumbnailCache';

const LookPreviewCanvas = memo(({ look, width, height, transparent, allLooks, showGuides }) => {
  const canvasRef = useRef(null);
  const [refImg, setRefImg] = useState(null);
  const [canvasBgRefImg, setCanvasBgRefImg] = useState(null);
  const [overlayImg, setOverlayImg] = useState(null);
  const [canvasBgImg, setCanvasBgImg] = useState(null);
  const [collagePhotoImgs, setCollagePhotoImgs] = useState({});

  const lookW = look.canvas_width || 2400;
  const lookH = look.canvas_height || 3000;
  const lookAspect = lookW / lookH;
  const containerAspect = width / height;

  let cw, ch;
  if (lookAspect > containerAspect) { cw = width; ch = Math.round(width / lookAspect); }
  else { ch = height; cw = Math.round(height * lookAspect); }

  // Check for a cached rendered thumbnail
  const cachedUrl = look.id ? getCachedThumbnail(look.id, cw, ch) : null;

  // Load ref photo via downscaled cache — use thumb as fallback when full data is omitted
  const refPhotoSrc = look.ref_photo_data || look.ref_photo_thumb;
  useEffect(() => {
    if (cachedUrl) return;
    getRefImage(refPhotoSrc).then(img => { if (img) setRefImg(img); });
  }, [refPhotoSrc, cachedUrl]);

  // Load collage background reference image via downscaled cache
  useEffect(() => {
    if (cachedUrl) return;
    if (look.canvas_bg_ref_data) {
      getRefImage(look.canvas_bg_ref_data).then(img => { if (img) setCanvasBgRefImg(img); });
    } else {
      setCanvasBgRefImg(null);
    }
  }, [look.canvas_bg_ref_data, cachedUrl]);

  // Load S3 images
  useEffect(() => {
    if (cachedUrl) return;
    let cancelled = false;
    if (look.overlay_s3_path && look.overlay_filename) {
      const key = look.overlay_s3_path + (look.overlay_s3_path.endsWith('/') ? '' : '/') + look.overlay_filename;
      const cached = getCachedImage(key);
      if (cached) { setOverlayImg(cached); }
      else { loadImage(key).then(img => { if (!cancelled) setOverlayImg(img); }).catch(() => {}); }
    } else { setOverlayImg(null); }

    if (look.canvas_filepath && look.canvas_filename) {
      const key = look.canvas_filepath + (look.canvas_filepath.endsWith('/') ? '' : '/') + look.canvas_filename;
      const cached = getCachedImage(key);
      if (cached) { setCanvasBgImg(cached); }
      else { loadImage(key).then(img => { if (!cancelled) setCanvasBgImg(img); }).catch(() => {}); }
    } else { setCanvasBgImg(null); }
    return () => { cancelled = true; };
  }, [look.overlay_s3_path, look.overlay_filename, look.canvas_filepath, look.canvas_filename, look.look_type, cachedUrl]);

  // Load collage photo images (downscaled) — both look templates and database poses
  useEffect(() => {
    if (cachedUrl) return;
    if (look.look_type !== 'collage') return;
    (look.collage_photos || []).forEach(photo => {
      // Look template source
      if (photo.look_ref && photo.source_type !== 'database') {
        if (collagePhotoImgs[photo.look_ref]) return;
        if (!allLooks) return;
        const refLook = allLooks.find(l => l.name === photo.look_ref);
        if (!refLook) return;
        const refSrc = refLook.ref_photo_data || refLook.ref_photo_thumb;
        if (!refSrc) return;
        getRefImage(refSrc).then(img => {
          if (img) setCollagePhotoImgs(prev => ({ ...prev, [photo.look_ref]: { img, look: refLook } }));
        });
      }
      // Database pose source
      if (photo.source_type === 'database' && photo.pose_thumbnail_url) {
        const key = `db_${photo.pose_id}`;
        if (collagePhotoImgs[key]) return;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          setCollagePhotoImgs(prev => ({ ...prev, [key]: { img, look: null } }));
        };
        img.src = photo.pose_thumbnail_url;
      }
    });
  }, [look.collage_photos, look.look_type, allLooks, cachedUrl]);

  // Draw canvas (skip if cached thumbnail exists)
  useEffect(() => {
    if (cachedUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(ch * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    if (transparent) { ctx.clearRect(0, 0, cw, ch); }
    else { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, cw, ch); }

    if (canvasBgRefImg) { ctx.drawImage(canvasBgRefImg, 0, 0, cw, ch); }
    else if (canvasBgImg) { ctx.drawImage(canvasBgImg, 0, 0, cw, ch); }

    if (refImg) {
      const imgAspect = refImg.width / refImg.height;
      const canAspect = cw / ch;
      const refZoom = look.ref_zoom ?? 1;
      const refRotation = look.ref_rotation ?? 0;
      const refOffsetX = look.ref_offset_x ?? 0;
      const refOffsetY = look.ref_offset_y ?? 0;

      let dw, dh;
      if (imgAspect > canAspect) { dh = ch; dw = dh * imgAspect; }
      else { dw = cw; dh = dw / imgAspect; }
      dw *= refZoom;
      dh *= refZoom;

      const cx = cw / 2 + refOffsetX * cw;
      const cy = ch / 2 + refOffsetY * ch;

      ctx.save();
      ctx.translate(cx, cy);
      if (refRotation !== 0) ctx.rotate(refRotation * Math.PI / 180);
      ctx.globalAlpha = transparent ? 0.85 : 0.5;
      ctx.drawImage(refImg, -dw / 2, -dh / 2, dw, dh);
      ctx.globalAlpha = 1.0;
      ctx.restore();
    }

    if (look.look_type === 'collage') {
      const PHOTO_COLORS = ['#1976d2', '#e91e63', '#4caf50', '#ff9800', '#9c27b0'];
      (look.collage_photos || []).forEach((photo, i) => {
        if (photo.x == null || photo.width == null) return;
        const px = photo.x * cw, py = photo.y * ch, pw = photo.width * cw, ph = photo.height * ch;
        const color = PHOTO_COLORS[i % PHOTO_COLORS.length];
        const imgKey = photo.source_type === 'database' ? `db_${photo.pose_id}` : photo.look_ref;
        const photoData = imgKey ? collagePhotoImgs[imgKey] : null;
        if (photoData && photoData.img) {
          const pImg = photoData.img;
          ctx.save(); ctx.beginPath(); ctx.rect(px, py, pw, ph); ctx.clip();
          const iA = pImg.width / pImg.height, rA = pw / ph;
          let ddw, ddh;
          if (iA > rA) { ddh = ph; ddw = ddh * iA; } else { ddw = pw; ddh = ddw / iA; }
          ctx.drawImage(pImg, px + (pw - ddw) / 2, py + (ph - ddh) / 2, ddw, ddh);
          ctx.restore();
        } else {
          ctx.fillStyle = color + '20'; ctx.fillRect(px, py, pw, ph);
          ctx.strokeStyle = color + '60'; ctx.lineWidth = 1; ctx.strokeRect(px, py, pw, ph);
          const fs = Math.max(6, Math.min(10, pw * 0.15));
          ctx.fillStyle = color; ctx.font = `${fs}px sans-serif`; ctx.textAlign = 'center';
          const pLabel = photo.source_type === 'database' ? (photo.pose_name || 'DB') : (photo.look_ref || `P${i + 1}`);
          ctx.fillText(pLabel, px + pw / 2, py + ph / 2 + fs * 0.35);
          ctx.textAlign = 'start';
        }
      });
    }

    // Face placement oval — only shown when showGuides is true (e.g., layout slot previews)
    if (showGuides && look.eyes_y != null && look.face_height != null && look.center_x != null) {
      const fovCx = look.center_x * cw;
      const fovEy = look.eyes_y * ch;
      const fovFh = look.face_height * ch;
      const fovOvalCY = fovEy + fovFh * 0.1;
      const fovRx = fovFh * 0.35;
      const fovRy = fovFh / 2;
      const fovRot = (look.rotation || 0) * Math.PI / 180;
      ctx.save();
      ctx.translate(fovCx, fovOvalCY);
      ctx.rotate(fovRot);
      ctx.strokeStyle = 'rgba(233, 30, 99, 0.5)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 2]);
      ctx.beginPath();
      ctx.ellipse(0, 0, fovRx, fovRy, 0, 0, Math.PI * 2);
      ctx.stroke();
      const fovEyeLocalY = fovEy - fovOvalCY;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(-4, fovEyeLocalY);
      ctx.lineTo(4, fovEyeLocalY);
      ctx.moveTo(0, fovEyeLocalY - 4);
      ctx.lineTo(0, fovEyeLocalY + 4);
      ctx.stroke();
      ctx.restore();
    }

    if (overlayImg) { ctx.drawImage(overlayImg, 0, 0, cw, ch); }

    (look.text_fields || []).forEach((tf) => {
      if (tf.center_x != null && tf.center_y != null) {
        const tx = tf.center_x * cw, ty = tf.center_y * ch;
        const left = (tf.bound_x_left || 0) * cw, right = (tf.bound_x_right || 1) * cw;
        const fs = Math.min(16, Math.max(6, (tf.font_size_max || 160) * ch / (look.canvas_height || 3000)));
        ctx.font = `${tf.italic ? 'italic ' : ''}${tf.bold ? 'bold ' : ''}${fs}px sans-serif`;
        ctx.fillStyle = tf.font_color || '#000';
        if (tf.shadow) { ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 2; ctx.shadowOffsetX = 1; ctx.shadowOffsetY = 1; }
        ctx.textAlign = tf.align || 'center';
        const ax = tf.align === 'left' ? left + 2 : tf.align === 'right' ? right - 2 : tx;
        ctx.fillText(tf.content || '', ax, ty + fs * 0.35);
        ctx.textAlign = 'start';
        if (tf.shadow) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0; }
      }
    });

    if (showGuides && !transparent && look.mascot_center_x != null && look.mascot_center_y != null && look.mascot_size != null) {
      const mx = look.mascot_center_x * cw, my = look.mascot_center_y * ch, ms = look.mascot_size * cw;
      ctx.strokeStyle = 'rgba(76, 175, 80, 0.5)'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]); ctx.strokeRect(mx - ms / 2, my - ms / 2, ms, ms); ctx.setLineDash([]);
    }

    // Cache the rendered thumbnail — only when all async images have loaded.
    // Without this guard, the first draw (before async images arrive) caches a
    // blank white canvas, which then poisons every subsequent render.
    const hasRefSrc = !!(look.ref_photo_data || look.ref_photo_thumb);
    const refReady = !hasRefSrc || refImg;
    const hasBgSrc = !!(look.canvas_bg_ref_data || (look.canvas_filepath && look.canvas_filename));
    const bgReady = !hasBgSrc || canvasBgRefImg || canvasBgImg;
    // Check collage photos are loaded
    const collagePhotos = (look.look_type === 'collage' && look.collage_photos) || [];
    const expectedPhotoKeys = collagePhotos
      .map(p => p.source_type === 'database' ? (p.pose_id ? `db_${p.pose_id}` : null) : (p.look_ref || null))
      .filter(Boolean);
    const collageReady = expectedPhotoKeys.length === 0 || expectedPhotoKeys.every(k => collagePhotoImgs[k]);
    const hasOverlaySrc = !!(look.overlay_s3_path && look.overlay_filename);
    const overlayReady = !hasOverlaySrc || overlayImg;
    if (look.id && !transparent && refReady && bgReady && collageReady && overlayReady) {
      try { setCachedThumbnail(look.id, cw, ch, canvas.toDataURL('image/png')); }
      catch (e) { /* tainted canvas */ }
    }
  }, [look, refImg, canvasBgRefImg, overlayImg, canvasBgImg, collagePhotoImgs, cw, ch, transparent, showGuides, cachedUrl]);

  // If cached, render an img tag instead of a canvas
  if (cachedUrl) {
    return (
      <img
        src={cachedUrl}
        width={cw}
        height={ch}
        alt={look.name || 'Look preview'}
        style={{ width: cw, height: ch, display: 'block', borderRadius: 2, margin: '0 auto' }}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={cw}
      height={ch}
      style={{ width: cw, height: ch, display: 'block', borderRadius: 2, margin: '0 auto' }}
    />
  );
});

LookPreviewCanvas.displayName = 'LookPreviewCanvas';

export default LookPreviewCanvas;
